/** The quick actions every value on the TOUR OPERATOR page offers — the
 *  related-records strip, the General tab's fields, the configuration change
 *  history, the integrations grid and dialog, and the preflight controls — so
 *  the same reference gets the same set wherever it is rendered. The Defaults and
 *  Pre-check-in tabs' URLs live here too (`operatorDefaultsHref`,
 *  `operatorPreCheckInHref`): other features link into them.
 *
 *  Every grid href comes from gridLinks; every action from ValueActions' `act`.
 *
 *  Id spaces this page lives with (see gridLinks for the full list):
 *   - a tenant is a PICKER param (`?tenant=<id>`) on the QuerySearch grids and
 *     a plain param on the rest, but `/notifications/features` and
 *     `/tour-operators?q=parent=` take the operator CODE (all-digits also
 *     matches the id), and `/bgtasks` spells it `tour_operator_id=`.
 *   - `/import-runs?q=source=` is the import-SOURCE code and is exact-only
 *     (the whole import search index is), as are `request=` there and on
 *     `/api-requests`; `/delivery-logs?q=request=` is iexact.
 *   - `/rbac/access-groups` has no `?tenant=` param at all — a group is reached
 *     by its own id or not at all.
 *   - `/api-requests` and `/delivery-logs` links must carry a `created` bound;
 *     the two builders add one, and the webhook/activity actions pass
 *     `createdAroundNode(ts)` so the window sits on the moment in question.
 */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  apiCredentialsHref,
  apiRequestsHref,
  auditHref,
  bgtasksHref,
  bookingsHref,
  changesHref,
  cond,
  createdAroundNode,
  deliveryLogsHref,
  extIdsHref,
  featureFlagsHref,
  idCond,
  importRunsHref,
  launchPoliciesHref,
  notificationsHref,
  ordersHref,
  sessionsHref,
  subscriptionsHref,
  tagCategoriesHref,
  tagsHref,
  tourOperatorsHref,
  toursHref,
  usersHref,
  vehiclesHref,
} from '@/utils/gridLinks';

/** Everything the operator's own links need: the picker id and the code the
 *  code-keyed grids (feature flags, franchise children) match on. */
export interface TenantCtx {
  id: number;
  code: string;
}

// ---- The related-records strip -------------------------------------------

/** One labelled bucket of the strip: a trigger whose popover lists the grids
 *  that bucket covers, all scoped to this operator. */
export interface RelatedGroup {
  key: string;
  label: string;
  actions: ValueAction[];
}

export function peopleActions(t: TenantCtx): ValueAction[] {
  return [
    act.filter(usersHref('staff', { tenant: t.id }), 'Staff users of this operator'),
    act.filter(usersHref('guests', { tenant: t.id }), 'Guests of this operator'),
    act.filter(sessionsHref({ tenant: t.id }), 'Sessions of this operator'),
  ];
}

export function commerceActions(t: TenantCtx): ValueAction[] {
  return [
    act.filter(bookingsHref([], t.id), 'Bookings of this operator'),
    act.filter(ordersHref([], t.id), 'Orders of this operator'),
  ];
}

export function catalogActions(t: TenantCtx): ValueAction[] {
  return [
    act.filter(toursHref([], t.id), 'Tours of this operator'),
    act.filter(tagsHref([], t.id), 'Tags of this operator'),
    act.filter(tagCategoriesHref({ tenant: t.id }), 'Tag categories of this operator'),
    act.filter(launchPoliciesHref({ tenant: t.id }), 'Launch policies of this operator'),
    act.filter(vehiclesHref({ tenant: t.id, view: 'list' }), 'Vehicles of this operator'),
  ];
}

export function messagingActions(t: TenantCtx): ValueAction[] {
  return [
    act.filter(deliveryLogsHref([], { tenant: t.id }), 'Delivery logs of this operator (7 days)'),
    act.filter(notificationsHref([], t.id), 'Notifications of this operator'),
    // The feature-flags grid keys the tenant by CODE (all-digits also matches the id).
    act.filter(featureFlagsHref([cond('tenant', t.code)]), 'Feature flags of this operator'),
  ];
}

export function integrationsActions(t: TenantCtx): ValueAction[] {
  return [
    act.filter(subscriptionsHref([], t.id), 'Webhook subscriptions of this operator'),
    act.filter(apiCredentialsHref([], t.id), 'API credentials of this operator'),
    act.filter(extIdsHref([], t.id), 'External-ID mappings of this operator'),
    act.filter(importRunsHref([], t.id), 'Import runs of this operator'),
    act.filter(apiRequestsHref([], { tenant: t.id }), 'API requests of this operator (7 days)'),
  ];
}

export function opsActions(t: TenantCtx): ValueAction[] {
  return [
    act.filter(bgtasksHref({ tour_operator_id: t.id }), 'Background tasks of this operator'),
    act.filter(auditHref({ tenant: t.id }), 'Audit log of this operator'),
    act.filter(changesHref({ tenant: t.id }), 'Record changes of this operator'),
  ];
}

/** The franchise licensees: the grid filters them by the parent's CODE. */
export function licenseesActions(t: TenantCtx): ValueAction[] {
  return [
    act.filter(tourOperatorsHref([cond('parent', t.code)]), 'Licensees of this operator'),
    act.copy(t.code, 'Copy operator code'),
  ];
}

/** The whole strip, in reading order. `isFranchise` adds the licensees bucket
 *  (a non-parent has none, and the filter would land on an empty grid). */
export function relatedGroups(t: TenantCtx, opts: { isFranchise?: boolean } = {}): RelatedGroup[] {
  const groups: RelatedGroup[] = [
    { key: 'people', label: 'People', actions: peopleActions(t) },
    { key: 'commerce', label: 'Bookings & orders', actions: commerceActions(t) },
    { key: 'catalog', label: 'Catalog', actions: catalogActions(t) },
    { key: 'messaging', label: 'Messaging', actions: messagingActions(t) },
    { key: 'integrations', label: 'Integrations', actions: integrationsActions(t) },
    { key: 'ops', label: 'Ops', actions: opsActions(t) },
  ];
  if (opts.isFranchise) {
    groups.push({ key: 'licensees', label: 'Licensees', actions: licenseesActions(t) });
  }
  return groups;
}

// ---- Payload fields the page never showed --------------------------------

/** The franchise parent: its own page, its other licensees, its code. */
export function parentActions(parent: { id: number; code?: string | null }): ValueAction[] {
  const out: ValueAction[] = [act.open(`/tour-operators/${parent.id}`, 'Open franchise parent')];
  if (parent.code) {
    out.push(
      act.filter(tourOperatorsHref([cond('parent', parent.code)]), 'All licensees of that parent'),
      act.copy(parent.code, 'Copy parent code'),
    );
  }
  return out;
}

/** The paired operator (SBX D8): a sandbox's production operator, or a production operator's sandbox
 *  (`<code>sbx`). Both are tenants of their own; the sandbox's state lives on its Sandbox tab. */
export function sandboxPairActions(
  pair: { id: number; code: string },
  role: 'production' | 'sandbox',
): ValueAction[] {
  return role === 'sandbox'
    ? [
        act.open(`/tour-operators/${pair.id}`, 'Open sandbox'),
        act.open(`/tour-operators/${pair.id}/sandbox`, 'Its sandbox state'),
        act.copy(pair.code, 'Copy sandbox code'),
      ]
    : [
        act.open(`/tour-operators/${pair.id}`, 'Open production operator'),
        act.filter(bookingsHref([], pair.id), "Production's bookings"),
        act.copy(pair.code, 'Copy production code'),
      ];
}

/** One licensee row of `children[]`. */
export function childActions(child: { id: number; code: string }): ValueAction[] {
  return [
    act.open(`/tour-operators/${child.id}`, 'Open licensee'),
    act.filter(bookingsHref([], child.id), "Licensee's bookings"),
    act.copy(child.code, 'Copy licensee code'),
  ];
}

/** The accounting company — the operators grid matches it by id or name. */
export function companyActions(company: { id: number; label?: string | null }): ValueAction[] {
  const out: ValueAction[] = [
    act.filter(tourOperatorsHref([cond('company', String(company.id))]), 'Operators of this company'),
    act.copy(String(company.id), 'Copy company id'),
  ];
  if (company.label) out.push(act.copy(company.label, 'Copy company name'));
  return out;
}

/** A default access group (staff or guest). `/rbac/access-groups` has no
 *  tenant param, so the group's own page is all there is to offer. */
export function accessGroupActions(group: { id: number; label?: string | null }): ValueAction[] {
  const out: ValueAction[] = [act.open(`/rbac/access-groups/${group.id}`, 'Open access group')];
  if (group.label) out.push(act.copy(group.label, 'Copy group name'));
  return out;
}

/** The default launch policy: `/launch-policies` has no `?edit=` intent, so
 *  the link lands on the operator's list and on the tours that use it. */
export function launchPolicyActions(
  policy: { id: number; label?: string | null },
  t: TenantCtx,
): ValueAction[] {
  return [
    act.filter(launchPoliciesHref({ tenant: t.id }), "Operator's launch policies"),
    act.filter(toursHref([idCond('launch_policy', policy.id)], t.id), 'Tours with this launch policy'),
    act.copy(String(policy.id), 'Copy launch policy id'),
  ];
}

/** A tag the operator carries (`tag_ids` — names are not in the payload). */
export function operatorTagActions(tagId: number, t: TenantCtx): ValueAction[] {
  return [
    act.open(`/tags/${tagId}`, 'Open tag'),
    act.filter(bookingsHref([idCond('tag', tagId)], t.id), 'Bookings with this tag'),
    act.copy(String(tagId), 'Copy tag id'),
  ];
}

/** A content reference the payload carries as an id + label pair (refund
 *  policy, review, photo sharing, app promo image). None of these has a console
 *  page, so the id and the name are all a value action can honestly offer. */
export function contentRefActions(id: number, label?: string | null): ValueAction[] {
  const out: ValueAction[] = [act.copy(String(id), 'Copy id')];
  if (label) out.push(act.copy(label, 'Copy name'));
  return out;
}

/** A URL the payload carries (logo, payment page, T&C, privacy policy…). */
export function urlActions(url: string, label = 'Open'): ValueAction[] {
  return [act.external(url, label), act.copy(url, 'Copy URL')];
}

// ---- The Defaults tab -----------------------------------------------------

/** A section of the operator's Defaults tab; its URL scrolls the tab to it. */
export type OperatorDefaultsSection = 'tag-categories' | 'trip-settings';

/** The operator's Defaults tab, or one of its two sections. */
export function operatorDefaultsHref(topId: number, section?: OperatorDefaultsSection): string {
  return `/tour-operators/${topId}/defaults${section ? `/${section}` : ''}`;
}

const DEFAULTS_LABELS: Record<OperatorDefaultsSection | 'tab', string> = {
  tab: "Operator's defaults",
  'tag-categories': "Operator's default tag categories",
  'trip-settings': "Operator's trip settings",
};

/** Where an operator default is changed — for a value that comes from one (a tour's
 *  inherited tag-category row). A licensee's Defaults tab lists its franchise parent's
 *  defaults too, so the tour's own operator is always the one to open. */
export function operatorDefaultsActions(
  topId: number,
  section?: OperatorDefaultsSection,
): ValueAction[] {
  return [act.open(operatorDefaultsHref(topId, section), DEFAULTS_LABELS[section ?? 'tab'])];
}

// ---- The Pre-check-in tab -------------------------------------------------

/** The operator's Pre-check-in tab: the form every tour that uses the operator's
 *  pre-check-in settings gets. */
export function operatorPreCheckInHref(topId: number): string {
  return `/tour-operators/${topId}/pre-check-in`;
}

// ---- General tab fields ---------------------------------------------------

export function codeActions(code: string): ValueAction[] {
  return [
    act.copy(code, 'Copy code'),
    act.filter(tourOperatorsHref([cond('code', code)]), 'This operator in the grid'),
    act.filter(featureFlagsHref([cond('tenant', code)]), 'Feature flags of this operator'),
  ];
}

export function emailActions(email: string): ValueAction[] {
  return [
    act.mailto(email),
    act.copy(email, 'Copy email'),
    act.filter(tourOperatorsHref([cond('email', email)]), 'Operators with this email'),
  ];
}

/** The "Is franchise parent" flag: the licensees it implies. */
export function franchiseFlagActions(t: TenantCtx): ValueAction[] {
  return [act.filter(tourOperatorsHref([cond('parent', t.code)]), 'Licensees of this operator')];
}

// ---- Configuration → change history --------------------------------------

export interface ConfigChangeActorRef {
  id: number;
  email?: string | null;
}

export function changeActorActions(actor: ConfigChangeActorRef): ValueAction[] {
  const out: ValueAction[] = [act.open(`/users/${actor.id}`, 'Open user')];
  if (actor.email) {
    out.push(
      act.mailto(actor.email),
      act.filter(auditHref({ actor_email: actor.email }), 'Audit entries by this actor'),
      act.copy(actor.email, 'Copy email'),
    );
  }
  return out;
}

/** The change's source ("console", "dashboard", "celery", …). The config-change
 *  enum is a subset of the /changes grid's own `source` options. */
export function changeSourceActions(source: string, t: TenantCtx): ValueAction[] {
  return [
    act.filter(changesHref({ tenant: t.id, source }), `Record changes via ${source}`),
    act.filter(changesHref({ tenant: t.id }), 'All record changes of this operator'),
  ];
}

export function ipActions(ip: string): ValueAction[] {
  return [act.copy(ip, 'Copy IP'), act.whois(ip)];
}

/** A correlation id: everything the same request/task touched. `request=` is
 *  exact on import-runs and api-requests, iexact on delivery-logs. */
export function correlationIdActions(correlationId: string, t: TenantCtx): ValueAction[] {
  return [
    act.copy(correlationId, 'Copy correlation id'),
    act.filter(
      apiRequestsHref([cond('request', correlationId, 'exact')], { tenant: t.id }),
      'API requests with this correlation id (7 days)',
    ),
    act.filter(
      importRunsHref([cond('request', correlationId, 'exact')], t.id),
      'Import runs with this correlation id',
    ),
    act.filter(
      deliveryLogsHref([cond('request', correlationId)], { tenant: t.id }),
      'Delivery logs with this correlation id (7 days)',
    ),
  ];
}

// ---- Integrations ---------------------------------------------------------

/** A restech codename: every grid that records which system a row came from. */
export function restechActions(restech: string, t: TenantCtx): ValueAction[] {
  return [
    act.filter(subscriptionsHref([cond('restech', restech)], t.id), 'Webhook subscriptions of this system'),
    act.filter(bookingsHref([cond('integration', restech)], t.id), 'Bookings from this system'),
    act.filter(extIdsHref([cond('integration', restech)], t.id), 'External-ID mappings of this system'),
    act.filter(
      apiRequestsHref([cond('integration', restech)], { tenant: t.id }),
      'API requests of this system (7 days)',
    ),
    act.filter(tourOperatorsHref([cond('restech', restech)]), 'Operators on this system'),
    act.copy(restech, 'Copy system codename'),
  ];
}

/** A fleet-management system codename — it has no restech grids behind it, so
 *  the vehicles it feeds are what the value can reach. */
export function fleetSystemActions(provider: string, t: TenantCtx): ValueAction[] {
  return [
    act.filter(vehiclesHref({ tenant: t.id, view: 'list' }), 'Vehicles of this operator'),
    act.copy(provider, 'Copy system codename'),
  ];
}

/** A restech ACCOUNT slug (the default account has none). */
export function accountActions(slug: string, t: TenantCtx): ValueAction[] {
  return [
    act.copy(slug, 'Copy account slug'),
    act.filter(subscriptionsHref([cond('account', slug)], t.id), 'Webhook subscriptions of this account'),
    act.filter(extIdsHref([cond('account', slug)], t.id), 'External-ID mappings of this account'),
  ];
}

/** An import-source code (`source_id` on the integration row). Exact-only —
 *  the import search index stores exact values. */
export function importSourceActions(sourceId: string, t: TenantCtx): ValueAction[] {
  return [
    act.copy(sourceId, 'Copy source id'),
    act.filter(importRunsHref([cond('source', sourceId, 'exact')], t.id), 'Import runs from this source'),
  ];
}

/** A webhook/pull moment: the API requests around it, plus the timestamp
 *  itself. `integration` narrows the window to this system's traffic. */
export function activityActions(
  ts: string,
  opts: { integration?: string | null; t: TenantCtx },
): ValueAction[] {
  const nodes = opts.integration ? [cond('integration', opts.integration)] : [];
  return [
    act.filter(
      apiRequestsHref(nodes, { tenant: opts.t.id, window: createdAroundNode(ts) }),
      'API requests around this time (±1h)',
    ),
    act.copy(ts, 'Copy timestamp'),
  ];
}

/** Who created the integration row (`created_by` — a user id, never rendered). */
export function createdByActions(userId: number): ValueAction[] {
  return [act.open(`/users/${userId}`, 'Open user'), act.copy(String(userId), 'Copy user id')];
}

/** The inbound shared secret. Only offered when the viewer may manage the
 *  integration — everyone else is served a mask, and copying a mask is a trap. */
export function sharedSecretActions(token: string, canManage: boolean): ValueAction[] {
  return canManage ? [act.copy(token, 'Copy shared secret')] : [];
}

/** A notification subscription row of the account panel. The row click still
 *  opens the drawer in place; this only adds the new-tab / copy actions. */
export function subscriptionActions(sub: { id: number; notification_type?: string | null }): ValueAction[] {
  const out: ValueAction[] = [act.open(`/subscriptions/${sub.id}`, 'Open subscription')];
  if (sub.notification_type) out.push(act.copy(sub.notification_type, 'Copy notification type'));
  return out;
}

// ---- Preflight ------------------------------------------------------------

/** The background task a data-sync item spawned (`context.bgtask`). */
export function bgtaskActions(taskId: string): ValueAction[] {
  return [act.open(`/bgtasks/${taskId}`, 'Open background task'), act.copy(taskId, 'Copy task id')];
}

/** A preflight user (run-log actor, assignment-group member, legacy owner). */
export function userActions(user: { id: number; email?: string | null }): ValueAction[] {
  const out: ValueAction[] = [act.open(`/users/${user.id}`, 'Open user')];
  if (user.email) out.push(act.mailto(user.email), act.copy(user.email, 'Copy email'));
  return out;
}

/** A team member of the onboarding team. The payload carries no user id, so
 *  the staff grid's email filter is how the account is reached. */
export function teamMemberActions(email: string, t: TenantCtx): ValueAction[] {
  return [
    act.mailto(email),
    act.filter(usersHref('staff', { tenant: t.id, email_contains: email }), 'This member in the staff grid'),
    act.copy(email, 'Copy email'),
  ];
}

/** A support contact — an email, a phone number, or something else entirely
 *  (a URL, a handle); only offer the scheme the value actually supports. */
export function supportContactActions(contact: string): ValueAction[] {
  const value = contact.trim();
  const out: ValueAction[] = [];
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) out.push(act.mailto(value));
  else if (/^\+?[\d\s().-]{5,}$/.test(value)) out.push(act.tel(value));
  else if (/^https?:\/\/\S+$/i.test(value)) out.push(act.external(value, 'Open link'));
  out.push(act.copy(value, 'Copy contact'));
  return out;
}

/** An import key (Import_Source) listed by the restech preflight actions. */
export function importKeyActions(code: string, t: TenantCtx): ValueAction[] {
  return [
    act.copy(code, 'Copy source code'),
    act.filter(importRunsHref([cond('source', code, 'exact')], t.id), 'Import runs from this key'),
  ];
}

/** An API credential listed by IMPORT_ACCESS_TOKEN_CHECK. */
export function apiCredentialActions(
  cred: { id: number; key_prefix?: string | null },
  t: TenantCtx,
): ValueAction[] {
  const out: ValueAction[] = [act.open(`/api-credentials/${cred.id}`, 'Open credential')];
  if (cred.key_prefix) {
    out.push(
      act.filter(apiCredentialsHref([cond('prefix', cred.key_prefix)], t.id), 'Credentials with this prefix'),
      act.copy(cred.key_prefix, 'Copy key prefix'),
    );
  }
  return out;
}

/** A tag-category code from TAG_CATEGORIES. The payload has no category id, so
 *  the operator's category list is as close as a link can get. */
export function tagCategoryCodeActions(code: string, t: TenantCtx): ValueAction[] {
  return [
    act.filter(tagCategoriesHref({ tenant: t.id }), "Operator's tag categories"),
    act.filter(tagsHref([cond('category', code)], t.id), 'Tags in this category'),
    act.copy(code, 'Copy category code'),
  ];
}

/** The fleet roster a FLEET_* item reports on. */
export function fleetVehiclesActions(t: TenantCtx): ValueAction[] {
  return [act.filter(vehiclesHref({ tenant: t.id, view: 'list' }), 'Vehicles of this operator')];
}

/** A value that is only ever copied (a hostname, a SID, a fingerprint, a key).
 *  A masked secret yields nothing — copying a mask helps no one. */
export function copyOnlyActions(value: string | null | undefined, label: string): ValueAction[] {
  const v = (value ?? '').trim();
  if (!v || isMasked(v)) return [];
  return [act.copy(v, label)];
}

/** The console serves credential values masked (`••••`, `***…`, `…`) to anyone
 *  without the manage permission; those strings must never become a copy. */
export function isMasked(value: string): boolean {
  return /^[•*•●.…\s]+$/.test(value) || value.includes('••');
}

/** The Twilio Conversations service, on Twilio's own console — the URL the
 *  backend's preflight resources use (`.../us1/service/conversations/<SID>`). */
export function twilioConversationsActions(serviceSid: string): ValueAction[] {
  return [
    act.external(`https://console.twilio.com/us1/service/conversations/${encodeURIComponent(serviceSid)}`, 'Open in the Twilio console'),
    act.copy(serviceSid, 'Copy service SID'),
  ];
}

/** The Firebase project, on the Firebase console — the URL the backend's
 *  preflight resources use (`.../project/<id>/overview`). */
export function firebaseProjectActions(projectId: string): ValueAction[] {
  return [
    act.external(`https://console.firebase.google.com/project/${encodeURIComponent(projectId)}/overview`, 'Open in the Firebase console'),
    act.copy(projectId, 'Copy project id'),
  ];
}

// ---- Batch config ---------------------------------------------------------

/** A tenant of the cross-tenant config table. The name is already an inline
 *  link to that operator's Configuration tab; this adds the rest. */
export function batchTenantActions(tenant: { id: number; code?: string | null }): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/tour-operators/${tenant.id}/configuration`, 'Open configuration'),
    act.filter(tourOperatorsHref([cond('id', String(tenant.id), 'exact')]), 'This operator in the grid'),
  ];
  if (tenant.code) out.push(act.copy(tenant.code, 'Copy operator code'));
  return out;
}
